import { useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router'
import { ArrowLeft, TriangleAlert, Info } from 'lucide-react'
import apiClient from '../../api/client'
import { getApiErrorMessage } from '../../api/errors'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const token = searchParams.get('token') || ''
  const email = searchParams.get('email') || ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [validationError, setValidationError] = useState('')

  const validate = (): boolean => {
    if (newPassword.length < 8) {
      setValidationError('La password deve contenere almeno 8 caratteri.')
      return false
    }
    if (newPassword !== confirmPassword) {
      setValidationError('Le password non coincidono.')
      return false
    }
    setValidationError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validate()) return

    setLoading(true)

    try {
      await apiClient.post('/auth/reset-password', { email, token, newPassword })
      navigate('/login', { state: { message: 'Password reimpostata con successo. Effettua il login.' } })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Errore durante il reset della password. Il link potrebbe essere scaduto.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="relative flex min-h-dvh w-full items-center justify-center overflow-hidden bg-surface p-4 text-heading">
      <div className="pointer-events-none absolute top-6 left-8 flex items-center gap-2.5">
        <img src="/apple-touch-icon.png" alt="" className="h-5 w-5 rounded-md" />
        <span className="font-mono text-xs tracking-wider text-link uppercase">DomusAurea</span>
      </div>
      <span className="pointer-events-none absolute top-6 right-8 font-mono text-xs tracking-wider text-link">Φ 1.618</span>
      <span className="pointer-events-none absolute right-8 bottom-6 font-mono text-xs tracking-wider text-link">© 2026 DomusAurea</span>
      <div className="pointer-events-none absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-heading/10" />
      <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-heading/10" />

      <section className="animate-panel-in relative z-10 my-8 w-full max-w-100 border border-border-subtle bg-canvas p-8 opacity-0 shadow-2xl sm:p-11">
        {!token || !email ? (
          <>
            <div className="mb-7">
              <p className="mb-2 font-mono text-[11px] tracking-[0.16em] text-notice uppercase">Recupero account</p>
              <h1 className="mb-2 text-2xl font-semibold [font-variant-caps:small-caps]">Link non valido</h1>
              <p className="mb-0 text-sm leading-relaxed text-muted">Il link per reimpostare la password non è valido o è incompleto. Puoi richiederne uno nuovo.</p>
            </div>
            <Link
              to="/forgot-password"
              className="flex h-13 w-full items-center justify-center gap-2 bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              Richiedi un nuovo link
            </Link>
          </>
        ) : (
          <>
            <div className="mb-7">
              <p className="mb-2 font-mono text-[11px] tracking-[0.16em] text-link uppercase">Recupero account</p>
              <h1 className="mb-2 text-2xl font-semibold [font-variant-caps:small-caps]">Reimposta password</h1>
              <p className="mb-0 text-sm leading-relaxed text-muted">Scegli una nuova password per il tuo account.</p>
            </div>

          {error && (
            <div className="mb-5 flex items-start gap-2.5 border border-danger-200 border-l-4 border-l-danger-500 bg-danger-50 px-3.5 py-3 text-sm leading-relaxed text-negative" role="alert">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {validationError && (
            <div className="mb-5 flex items-start gap-2.5 border border-warning-200 border-l-4 border-l-warning-500 bg-warning-50 px-3.5 py-3 text-sm leading-relaxed text-notice" role="alert">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{validationError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="newPassword" className="text-xs tracking-wide text-muted uppercase">Nuova password</label>
              <input
                type="password"
                className="h-12 w-full border border-border-subtle bg-surface px-4 text-base text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none sm:text-sm"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
                autoComplete="new-password"
                placeholder="Minimo 8 caratteri"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirmPassword" className="text-xs tracking-wide text-muted uppercase">Conferma password</label>
              <input
                type="password"
                className="h-12 w-full border border-border-subtle bg-surface px-4 text-base text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none sm:text-sm"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="flex h-13 w-full items-center justify-center gap-2 bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-75 disabled:shadow-none disabled:hover:translate-y-0"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-secondary-50/35 border-t-secondary-50" role="status" aria-hidden="true" />
                  Reimpostazione in corso...
                </>
              ) : (
                'Reimposta password'
              )}
            </button>
          </form>

          <div className="pt-1 text-center">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-heading hover:underline">
              <ArrowLeft className="h-3.5 w-3.5" />Torna al login
            </Link>
          </div>
          </>
        )}
      </section>
    </main>
  )
}
